export type AdLayoutShape =
  | "horizontal-billboard"
  | "horizontal-leaderboard"
  | "horizontal-inline"
  | "box-mpu"
  | "vertical-skyscraper"
  | "hero-lead"
  | "native-card"
  | "newsletter-bar";

export type AdLayoutSpec = {
  placement: string;
  name: string;
  shortLabel: string;
  layoutCategory: string;
  iabStandard: string;
  dimensions: {
    desktop: string;
    tablet?: string;
    mobile: string;
    primaryPx: { width: number; height: number };
  };
  aspectRatio: string;
  shape: AdLayoutShape;
  description: string;
  responsiveBehavior: string;
  compatibleFormats: Array<"image" | "text" | "html">;
  badgeColor: {
    bg: string;
    text: string;
    border: string;
  };
};

export const AD_LAYOUT_SPECS: Record<string, AdLayoutSpec> = {
  homepage_top: {
    placement: "homepage_top",
    name: "Homepage Top Billboard",
    shortLabel: "Top Billboard",
    layoutCategory: "Display Mega Banner",
    iabStandard: "IAB Billboard (970×250)",
    dimensions: {
      desktop: "970 × 250 px",
      tablet: "728 × 90 px",
      mobile: "320 × 100 px",
      primaryPx: { width: 970, height: 250 }
    },
    aspectRatio: "3.88 : 1 (Panoramic)",
    shape: "horizontal-billboard",
    description: "Premium top-of-page masthead billboard. Captures highest initial pageview visibility above the editorial fold.",
    responsiveBehavior: "Scales fluidly on desktop up to 970px, transitions to 728×90 on tablet and 320×100 mobile banner.",
    compatibleFormats: ["image", "text", "html"],
    badgeColor: { bg: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-200" }
  },
  homepage_hero: {
    placement: "homepage_hero",
    name: "Homepage Hero Lead Slot",
    shortLabel: "Hero Lead Display",
    layoutCategory: "Featured Hero Banner",
    iabStandard: "IAB Half-Page / Hero Feature (1200×380)",
    dimensions: {
      desktop: "1200 × 380 px",
      tablet: "768 × 300 px",
      mobile: "360 × 200 px",
      primaryPx: { width: 1200, height: 380 }
    },
    aspectRatio: "3.15 : 1 (Wide Feature)",
    shape: "hero-lead",
    description: "High-impact hero placement integrated alongside the primary breaking editorial lead.",
    responsiveBehavior: "Full bleed with 1200px max bounds, retaining high visual hierarchy.",
    compatibleFormats: ["image", "text", "html"],
    badgeColor: { bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200" }
  },
  homepage_inline: {
    placement: "homepage_inline",
    name: "Homepage Inline Break",
    shortLabel: "Inline Interstitial",
    layoutCategory: "In-Feed Panorama Banner",
    iabStandard: "IAB Super Leaderboard (970×150)",
    dimensions: {
      desktop: "970 × 150 px",
      tablet: "728 × 90 px",
      mobile: "320 × 80 px",
      primaryPx: { width: 970, height: 150 }
    },
    aspectRatio: "6.46 : 1 (Ultra-Slim Strip)",
    shape: "horizontal-inline",
    description: "Full-width editorial divider strip placed between major home category modules.",
    responsiveBehavior: "Fluid container scaling dynamically with content flow.",
    compatibleFormats: ["image", "text", "html"],
    badgeColor: { bg: "bg-blue-50", text: "text-blue-800", border: "border-blue-200" }
  },
  homepage_sponsored: {
    placement: "homepage_sponsored",
    name: "Homepage Sponsored Spotlight Feed",
    shortLabel: "Sponsored Card Grid",
    layoutCategory: "Native Editorial Grid",
    iabStandard: "Native Sponsored Content Card (Fluid 4:3)",
    dimensions: {
      desktop: "380 × 280 px (Per Card)",
      tablet: "340 × 260 px",
      mobile: "100% Fluid Width",
      primaryPx: { width: 380, height: 280 }
    },
    aspectRatio: "4 : 3 (Card Aspect)",
    shape: "native-card",
    description: "Multi-column native editorial cards with sponsor brand badge, headline, summary, and CTA button.",
    responsiveBehavior: "Responsive 3-column desktop grid collapsing to single-column card list on mobile.",
    compatibleFormats: ["text", "html", "image"],
    badgeColor: { bg: "bg-purple-50", text: "text-purple-800", border: "border-purple-200" }
  },
  article_top: {
    placement: "article_top",
    name: "Article Top Header",
    shortLabel: "Article Header",
    layoutCategory: "Article Masthead Leaderboard",
    iabStandard: "IAB Leaderboard (728×90)",
    dimensions: {
      desktop: "728 × 90 px",
      tablet: "728 × 90 px",
      mobile: "320 × 50 px (Mobile Leaderboard)",
      primaryPx: { width: 728, height: 90 }
    },
    aspectRatio: "8.08 : 1 (Slim Leaderboard)",
    shape: "horizontal-leaderboard",
    description: "Positioned directly above the article headline and byline metadata block.",
    responsiveBehavior: "Centered responsive banner with auto-margin centering.",
    compatibleFormats: ["image", "text", "html"],
    badgeColor: { bg: "bg-teal-50", text: "text-teal-800", border: "border-teal-200" }
  },
  article_middle: {
    placement: "article_middle",
    name: "Article Mid-Prose Break",
    shortLabel: "Mid-Article Break",
    layoutCategory: "In-Article Content Break",
    iabStandard: "IAB Medium Rectangle (300×250) / Leaderboard (728×90)",
    dimensions: {
      desktop: "728 × 90 px or 650 × 180 px",
      tablet: "600 × 150 px",
      mobile: "300 × 250 px (Inline MPU)",
      primaryPx: { width: 728, height: 90 }
    },
    aspectRatio: "8.08 : 1 / 1.2 : 1",
    shape: "horizontal-inline",
    description: "Native or banner insertion point embedded midway within long-form prose paragraphs.",
    responsiveBehavior: "Centers inside article prose container with soft divider margins.",
    compatibleFormats: ["image", "text", "html"],
    badgeColor: { bg: "bg-indigo-50", text: "text-indigo-800", border: "border-indigo-200" }
  },
  article_bottom: {
    placement: "article_bottom",
    name: "Article Footer End",
    shortLabel: "Article Outro Banner",
    layoutCategory: "Post-Article Outro Strip",
    iabStandard: "IAB Super Leaderboard / Billboard (970×250)",
    dimensions: {
      desktop: "970 × 200 px",
      tablet: "728 × 90 px",
      mobile: "320 × 100 px",
      primaryPx: { width: 970, height: 200 }
    },
    aspectRatio: "4.85 : 1 (Wide Outro)",
    shape: "horizontal-billboard",
    description: "End-of-article engagement strip placed right before comments and recommended stories.",
    responsiveBehavior: "Full column width with background framing.",
    compatibleFormats: ["image", "text", "html"],
    badgeColor: { bg: "bg-sky-50", text: "text-sky-800", border: "border-sky-200" }
  },
  sidebar: {
    placement: "sidebar",
    name: "Sidebar Rail",
    shortLabel: "Sidebar MPU / Skyscraper",
    layoutCategory: "Vertical Column Rail",
    iabStandard: "IAB Medium Rectangle (300×250) / Half-Page (300×600)",
    dimensions: {
      desktop: "300 × 250 px or 300 × 600 px",
      tablet: "300 × 250 px",
      mobile: "300 × 250 px (Stacked)",
      primaryPx: { width: 300, height: 250 }
    },
    aspectRatio: "1.2 : 1 (MPU Box) or 1 : 2 (Half Page)",
    shape: "box-mpu",
    description: "Sticky or static vertical rail ad located in the right-hand sidebar alongside article and archive feeds.",
    responsiveBehavior: "Fixed 300px width on desktop sidebar, collapses below content on small viewports.",
    compatibleFormats: ["image", "text", "html"],
    badgeColor: { bg: "bg-orange-50", text: "text-orange-800", border: "border-orange-200" }
  },
  category_page: {
    placement: "category_page",
    name: "Category Hub Banner",
    shortLabel: "Category Section Hub",
    layoutCategory: "Archive Header Billboard",
    iabStandard: "IAB Billboard (970×200)",
    dimensions: {
      desktop: "970 × 200 px",
      tablet: "728 × 90 px",
      mobile: "320 × 100 px",
      primaryPx: { width: 970, height: 200 }
    },
    aspectRatio: "4.85 : 1 (Wide Header)",
    shape: "horizontal-billboard",
    description: "Display banner positioned under category filter navigation tabs and topic hero banners.",
    responsiveBehavior: "Centers within category container with subtle top/bottom borders.",
    compatibleFormats: ["image", "text", "html"],
    badgeColor: { bg: "bg-rose-50", text: "text-rose-800", border: "border-rose-200" }
  },
  newsletter: {
    placement: "newsletter",
    name: "Newsletter Email Dispatch",
    shortLabel: "Email Digest Sponsor",
    layoutCategory: "Email Digest Sponsor Block",
    iabStandard: "Email Sponsor Banner (600×200)",
    dimensions: {
      desktop: "600 × 200 px",
      tablet: "600 × 200 px",
      mobile: "100% Fluid Width",
      primaryPx: { width: 600, height: 200 }
    },
    aspectRatio: "3 : 1 (Email Standard)",
    shape: "newsletter-bar",
    description: "High-conversion sponsor placement embedded inside weekly email newsletters and subscriber alerts.",
    responsiveBehavior: "Fluid email-safe container with fixed 600px desktop max-width.",
    compatibleFormats: ["text", "image", "html"],
    badgeColor: { bg: "bg-violet-50", text: "text-violet-800", border: "border-violet-200" }
  }
};

export function getAdLayoutSpec(placement: string): AdLayoutSpec {
  return (
    AD_LAYOUT_SPECS[placement] || {
      placement,
      name: placement.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      shortLabel: "Custom Placement",
      layoutCategory: "Custom Display",
      iabStandard: "Custom Standard Size",
      dimensions: {
        desktop: "Fluid Responsive",
        mobile: "Fluid Responsive",
        primaryPx: { width: 728, height: 90 }
      },
      aspectRatio: "Custom Ratio",
      shape: "horizontal-leaderboard",
      description: "Custom commercial placement slot.",
      responsiveBehavior: "Dynamic fluid container.",
      compatibleFormats: ["image", "text", "html"],
      badgeColor: { bg: "bg-slate-50", text: "text-slate-800", border: "border-slate-200" }
    }
  );
}
